<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import {
  productCartItemAvailabilityMode,
  productCartItemLabels,
  productCartItemTheme,
  productCartItemTrustCards
} from '../product-cart-item.config.js';
import { buildReserveUrl } from '../helpers/product-page-data.js';
import {
  f2rClearStaleSearchPeriodStorage,
  f2rReadSearchPeriod,
  f2rTodayYmd
} from '../../reservation/helpers/rental-search-prefill.js';
import {
  normalizePickupDate,
  normalizeRentalDateRange,
  normalizeReturnDate
} from '../../reservation/helpers/rental-dates.js';
import { HOME_WIDGET_CLOSED_DATES } from '../../../shared/closed-dates.js';
import VsbDateField from '../../home/VsbDateField.vue';

const props = defineProps({
  product: { type: Object, required: true },
  reserveFormBase: { type: String, default: '' },
  rentalPeriodUrlSuffix: { type: String, default: '' },
  pickupLocations: { type: Array, default: () => [] },
  defaultPickupLocation: { type: String, default: 'culemborg' },
  availabilityMode: { type: String, default: productCartItemAvailabilityMode },
  labels: { type: Object, default: () => productCartItemLabels },
  trustCards: { type: Array, default: () => productCartItemTrustCards },
  showRentalDates: { type: Boolean, default: true }
});

const showAvailability = computed(() => props.availabilityMode !== 'hidden');

const availabilityText = computed(() => {
  if (props.availabilityMode === 'guaranteed' && props.product.stockAvailable) {
    return props.labels.availabilityGuaranteed;
  }
  return props.labels.availabilityOnReserve;
});

const availabilityIsGuaranteed = computed(
  () => props.availabilityMode === 'guaranteed' && props.product.stockAvailable
);

const fromDate = ref('');
const toDate = ref('');
const pickupLocation = ref(props.defaultPickupLocation);
const pickupIsLocked = computed(() => (props.pickupLocations || []).length === 1);
const selectedPickupLabel = computed(() => {
  const id = pickupLocation.value;
  const match = (props.pickupLocations || []).find((loc) => loc.id === id);
  if (match && match.label) return match.label;
  if ((props.pickupLocations || []).length === 1) return props.pickupLocations[0].label || id;
  return id === 'beesd' ? 'Beesd' : 'Culemborg';
});

const closedDates = [...HOME_WIDGET_CLOSED_DATES];
/** VSB gesloten op zondag; productpagina blokkeert ophalen én inleveren op zondag. */
const pickupDisabledWeekdays = [0];
const returnDisabledWeekdays = [0];
const dateRangeOpts = { allowSundayReturn: false };

const todayStr = () => f2rTodayYmd();

const minDate = computed(() => todayStr());
const minReturnDate = computed(() => fromDate.value || minDate.value);

onMounted(() => {
  f2rClearStaleSearchPeriodStorage();
  const period = f2rReadSearchPeriod();
  if (period) {
    const normalized = normalizeRentalDateRange(
      period.from_date || '',
      period.to_date || '',
      dateRangeOpts
    );
    if (normalized.pickupDate && normalized.pickupDate >= todayStr()) {
      fromDate.value = normalized.pickupDate;
      toDate.value = normalized.returnDate;
    }
  }
});

watch(fromDate, (pickup) => {
  if (!pickup) return;
  const normalizedPickup = normalizePickupDate(pickup);
  if (normalizedPickup !== pickup) {
    fromDate.value = normalizedPickup;
    return;
  }
  const normalized = normalizeRentalDateRange(pickup, toDate.value, dateRangeOpts);
  if (normalized.returnDate !== toDate.value) {
    toDate.value = normalized.returnDate;
  }
});

watch(toDate, (ret) => {
  if (!fromDate.value) return;
  if (ret) {
    const normalizedReturn = normalizeReturnDate(ret, false);
    if (normalizedReturn !== ret) {
      toDate.value = normalizedReturn;
      return;
    }
  }
  const normalized = normalizeRentalDateRange(fromDate.value, ret || '', dateRangeOpts);
  if (normalized.returnDate !== toDate.value) {
    toDate.value = normalized.returnDate;
  }
});

watch(
  () => [fromDate.value, toDate.value],
  () => {
    try {
      if (fromDate.value && fromDate.value >= todayStr()) {
        sessionStorage.setItem(
          'vsb_rental_period',
          JSON.stringify({ from_date: fromDate.value, to_date: toDate.value || '' })
        );
      } else if (!fromDate.value) {
        sessionStorage.removeItem('vsb_rental_period');
      }
    } catch {
      /* ignore */
    }
  }
);

const reserveUrl = computed(() =>
  buildReserveUrl(
    props.reserveFormBase + props.product.productId,
    props.product.productId,
    fromDate.value,
    toDate.value,
    props.rentalPeriodUrlSuffix
  )
);

const themeVars = computed(() => ({
  '--vsb-pcart-bg': productCartItemTheme.cardBg,
  '--vsb-pcart-border': productCartItemTheme.cardBorder,
  '--vsb-pcart-radius': productCartItemTheme.cardRadius,
  '--vsb-pcart-shadow': productCartItemTheme.cardShadow,
  '--vsb-pcart-title': productCartItemTheme.titleColor,
  '--vsb-pcart-price': productCartItemTheme.priceAccent,
  '--vsb-pcart-muted': productCartItemTheme.priceMuted,
  '--vsb-pcart-btn-bg': productCartItemTheme.btnPrimaryBg,
  '--vsb-pcart-btn-fg': productCartItemTheme.btnPrimaryColor,
  '--vsb-pcart-trust-bg': productCartItemTheme.trustBg,
  '--vsb-pcart-trust-border': productCartItemTheme.trustBorder
}));
</script>

<template>
  <aside class="vsb-product-cart-item" :style="themeVars">
    <div class="vsb-product-cart-item__card">
      <h2 class="vsb-product-cart-item__title">{{ labels.sidebarTitle }}</h2>

      <div v-if="product.fromPrice.amount" class="vsb-product-cart-item__pricing">
        <div class="vsb-product-cart-item__from">
          <span class="vsb-product-cart-item__from-label">{{ labels.fromLabel }}</span>
          <p class="vsb-product-cart-item__from-amount">{{ product.fromPrice.amount }}</p>
          <span class="vsb-product-cart-item__from-suffix">{{ product.fromPrice.suffix }}</span>
        </div>
        <dl v-if="product.priceTiers.length" class="vsb-product-cart-item__tiers">
          <div v-for="(tier, i) in product.priceTiers" :key="i" class="vsb-product-cart-item__tier">
            <dt class="vsb-product-cart-item__tier-text">
              <span class="vsb-product-cart-item__tier-label">{{ tier.label }}</span>
              <span v-if="tier.detail" class="vsb-product-cart-item__tier-detail">{{ tier.detail }}</span>
            </dt>
            <dd class="vsb-product-cart-item__tier-amount">{{ tier.amount }}</dd>
          </div>
        </dl>
      </div>

      <div v-if="showRentalDates" class="vsb-product-cart-item__field">
        <label class="vsb-product-cart-item__label">{{ labels.rentalPeriod }}</label>
        <div class="vsb-product-cart-item__dates">
          <VsbDateField
            id="vsb-pcart-from"
            v-model="fromDate"
            :min="minDate"
            :disable="closedDates"
            :disable-weekdays="pickupDisabledWeekdays"
            class="vsb-product-cart-item__date-field"
          />
          <span class="vsb-product-cart-item__dates-arrow" aria-hidden="true">→</span>
          <VsbDateField
            id="vsb-pcart-to"
            v-model="toDate"
            :min="minReturnDate"
            :disable="closedDates"
            :disable-weekdays="returnDisabledWeekdays"
            class="vsb-product-cart-item__date-field"
          />
        </div>
      </div>

      <div v-if="pickupLocations.length" class="vsb-product-cart-item__field">
        <label class="vsb-product-cart-item__label" for="vsb-pickup-location">{{ labels.pickupLocation }}</label>
        <p v-if="pickupIsLocked" class="vsb-product-cart-item__pickup-fixed">{{ selectedPickupLabel }}</p>
        <select
          v-else
          id="vsb-pickup-location"
          v-model="pickupLocation"
          class="vsb-product-cart-item__select"
        >
          <option v-for="loc in pickupLocations" :key="loc.id" :value="loc.id">
            {{ loc.label }}
          </option>
        </select>
      </div>

      <p
        v-if="showAvailability"
        class="vsb-product-cart-item__stock"
        :class="{ 'vsb-product-cart-item__stock--guaranteed': availabilityIsGuaranteed }"
      >
        <span aria-hidden="true">{{ availabilityIsGuaranteed ? '✓' : 'ℹ' }}</span>
        {{ availabilityText }}
      </p>

      <a :href="reserveUrl" class="vsb-product-cart-item__btn vsb-product-cart-item__btn--primary">
        {{ labels.reserve }}
        <span aria-hidden="true">›</span>
      </a>
    </div>

    <ul class="vsb-product-cart-item__trust">
      <li v-for="(card, i) in trustCards" :key="i">
        <span class="vsb-product-cart-item__trust-icon" aria-hidden="true">✓</span>
        <span>
          <strong>{{ card.title }}</strong>
          <span v-if="card.text">{{ card.text }}</span>
        </span>
      </li>
    </ul>
  </aside>
</template>

<style scoped>
.vsb-product-cart-item {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
}

.vsb-product-cart-item__card {
  background: var(--vsb-pcart-bg, #fff);
  border: 1px solid var(--vsb-pcart-border, #e8ecf0);
  border-radius: var(--vsb-pcart-radius, 12px);
  box-shadow: var(--vsb-pcart-shadow, 0 4px 20px rgba(15, 23, 42, 0.1));
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.vsb-product-cart-item__title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 800;
  color: var(--vsb-pcart-title, #0b3d91);
}

.vsb-product-cart-item__pricing {
  display: grid;
  grid-template-columns: minmax(88px, auto) minmax(0, 1fr);
  gap: 0.65rem 0.75rem;
  align-items: start;
}

.vsb-product-cart-item__from-label {
  font-size: 0.75rem;
  color: var(--vsb-pcart-muted, #64748b);
}

.vsb-product-cart-item__from-amount {
  margin: 0.1rem 0 0;
  font-size: 1.65rem;
  font-weight: 800;
  color: var(--vsb-pcart-price, #3d9916);
  line-height: 1.1;
}

.vsb-product-cart-item__from-suffix {
  font-size: 0.85rem;
  color: var(--vsb-pcart-muted, #64748b);
}

.vsb-product-cart-item__tiers {
  margin: 0;
  padding: 0.5rem 0.65rem;
  background: #f1f5f9;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.82rem;
  min-width: 0;
}

.vsb-product-cart-item__tier {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.1rem 0.75rem;
  align-items: start;
}

.vsb-product-cart-item__tier-text {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.08rem;
  min-width: 0;
}

.vsb-product-cart-item__tier-label {
  font-weight: 400;
  color: #334155;
  line-height: 1.3;
}

.vsb-product-cart-item__tier-detail {
  font-size: 0.92em;
  font-weight: 400;
  color: #64748b;
  line-height: 1.3;
}

.vsb-product-cart-item__tier-amount {
  margin: 0;
  font-weight: 700;
  color: #0f172a;
  white-space: nowrap;
  text-align: right;
  line-height: 1.3;
}

.vsb-product-cart-item__label {
  display: block;
  font-weight: 700;
  font-size: 0.9rem;
  margin-bottom: 0.4rem;
  color: #0f172a;
}

.vsb-product-cart-item__dates {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 0.35rem;
  align-items: center;
}

.vsb-product-cart-item__date-field {
  min-width: 0;
}

.vsb-product-cart-item__date-field :deep(.vsb-date-field__alt),
.vsb-product-cart-item__date-field :deep(input.vsb-home-search__date) {
  font-size: 0.82rem;
  padding: 0.55rem 36px 0.55rem 0.6rem;
  min-height: 40px;
}

.vsb-product-cart-item__date-field :deep(.vsb-date-field__icon-btn) {
  width: 30px;
  height: 30px;
  right: 2px;
}

.vsb-product-cart-item__select {
  width: 100%;
  padding: 0.55rem 0.6rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.88rem;
  box-sizing: border-box;
}

.vsb-product-cart-item__pickup-fixed {
  margin: 0;
  padding: 0.55rem 0.6rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  font-size: 0.88rem;
  font-weight: 600;
  color: #0f172a;
  box-sizing: border-box;
}

.vsb-product-cart-item__dates-arrow {
  color: #64748b;
  font-size: 0.9rem;
}

.vsb-product-cart-item__stock {
  margin: 0;
  color: #475569;
  font-weight: 500;
  font-size: 0.88rem;
  line-height: 1.4;
}

.vsb-product-cart-item__stock--guaranteed {
  color: var(--vsb-pcart-price, #3d9916);
  font-weight: 600;
}

.vsb-product-cart-item__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  width: 100%;
  padding: 0.7rem 1rem;
  font-size: 0.95rem;
  font-weight: 700;
  text-decoration: none;
  border-radius: 8px;
  cursor: pointer;
  box-sizing: border-box;
}

.vsb-product-cart-item__btn--primary {
  background: var(--vsb-pcart-btn-bg, #3d9916);
  color: var(--vsb-pcart-btn-fg, #fff);
  border: none;
}

.vsb-product-cart-item__btn--secondary {
  background: #fff;
  color: var(--vsb-pcart-title, #0b3d91);
  border: 2px solid #cbd5e1;
}

.vsb-product-cart-item__trust {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.vsb-product-cart-item__trust li {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  padding: 0.75rem 0.85rem;
  background: var(--vsb-pcart-trust-bg, #f8fafc);
  border: 1px solid var(--vsb-pcart-trust-border, #e8ecf0);
  border-radius: 8px;
  font-size: 0.82rem;
  color: #475569;
}

.vsb-product-cart-item__trust li strong {
  display: block;
  color: #0f172a;
  font-weight: 700;
}

.vsb-product-cart-item__trust-icon {
  color: var(--vsb-pcart-price, #3d9916);
  font-weight: 700;
  flex-shrink: 0;
}
</style>
